import { eCountries } from '@logicalcommander/types'
import i18n from '../init/beforeAuth/language/i18n'

export const padNumberLeadingZero = (num: number, size: number): string => {
  const s = '0000000' + num
  return s.substr(s.length - size)
}

export const roundNumberDigitsAddPercentage = (num: number): string => {
  return `${(Math.round(num) * 100) / 100} %`
}

export const objectExists = (arrayOfObjects: any[], key: string, value: string): boolean => {
  return arrayOfObjects.some((obj) => obj.key === key && obj.value === value)
}

export const removeObjectByKeyValuePair = <T>(arr: T[], keyToDelete: keyof T, valueToDelete: T[keyof T]): T[] => {
  return [...arr.filter((obj) => obj[keyToDelete] !== valueToDelete)]
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Get localized country name by country code using Intl.DisplayNames API
 * @param code - ISO 3166-1 alpha-2 country code (e.g., 'US', 'ES', 'FR')
 * @param locale - Optional locale code (e.g., 'en', 'es'). Defaults to 'en' or current i18n language if available
 * @returns Localized country name or undefined if code is not found
 */
export const getCountryNameByCode = (code: string, locale?: string): string | undefined => {
  if (!code) return undefined

  // Try to get locale from i18n if not provided
  let currentLocale = locale
  if (!currentLocale && i18n && i18n.language) {
    currentLocale = i18n.language
  }

  // Default to 'en' if no locale is available
  currentLocale = currentLocale || 'en'

  try {
    // Use Intl.DisplayNames API for localized country names
    const displayNames = new Intl.DisplayNames([currentLocale], { type: 'region' })
    const localizedName = displayNames.of(code)

    // If Intl.DisplayNames returns the code itself, it might not be recognized
    // Fallback to enum lookup
    if (localizedName && localizedName !== code) {
      return localizedName
    }
  } catch (error) {
    // If Intl.DisplayNames fails, fallback to enum
    console.warn('Intl.DisplayNames not available, falling back to enum:', error)
  }

  // Fallback to enum lookup if Intl.DisplayNames is not available or fails
  const countryEntries = Object.entries(eCountries)
  for (const [countryName, countryCode] of countryEntries) {
    if (countryCode === code) {
      return countryName
    }
  }

  return undefined
}

export const decodeJwtJti = (token: string): string | null => {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).jti ?? null
  } catch {
    return null
  }
}

/** True when the admin JWT was issued for a remote support session. */
export const decodeJwtIsSupportSession = (token: string): boolean => {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))?.isSupportSession === true
  } catch {
    return false
  }
}

export const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1]
    const exp = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).exp
    return typeof exp === 'number' ? exp : null
  } catch {
    return null
  }
}
